import { useEffect, useState } from "react";
import { getBackendApiUrl } from "../config/backend";
import type { ActiveVehicle } from "../components/chat/VehicleHeaderBar";

/**
 * TRAC-905: the header's Shop Tires link is built by the server (GET /api/ymmv/shop-url, the
 * same builder as the agent's tire-search cards), never assembled here. A vehicle that already
 * carries a server-built `shopUrl` (a selector pick) uses it as-is; every other header vehicle
 * (product-page context, agent cards, session restore, a custom size) asks the server once.
 */

type LinkFields = Pick<ActiveVehicle, "year" | "make" | "model" | "variant" | "tireSize" | "frontSize" | "rearSize">;

/** Resolved links by request key; `null` = the server had no link for it (no parseable size). */
const cache = new Map<string, string | null>();
const inflight = new Map<string, Promise<string | null>>();

export function hasShopSize(v: LinkFields): boolean {
  return !!(v.tireSize || (v.frontSize && v.rearSize));
}

export function shopUrlQuery(v: LinkFields): string {
  const params = new URLSearchParams();
  if (v.year) params.set("year", String(v.year));
  if (v.make) params.set("make", v.make);
  if (v.model) params.set("model", v.model);
  if (v.variant) params.set("variant", v.variant);
  if (v.frontSize && v.rearSize) {
    params.set("front_size", v.frontSize);
    params.set("rear_size", v.rearSize);
  } else if (v.tireSize) {
    params.set("size", v.tireSize);
  }
  return params.toString();
}

export function fetchShopUrl(v: LinkFields): Promise<string | null> {
  const key = shopUrlQuery(v);
  if (cache.has(key)) return Promise.resolve(cache.get(key) ?? null);
  const pending = inflight.get(key);
  if (pending) return pending;
  const request = fetch(`${getBackendApiUrl()}/api/ymmv/shop-url?${key}`)
    .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
    .then((data: { shop_url?: string | null }) => {
      const url = typeof data?.shop_url === "string" && data.shop_url ? data.shop_url : null;
      cache.set(key, url);
      return url;
    })
    // A failed lookup isn't cached, so it is retried when the header's vehicles next change;
    // meanwhile no link is shown rather than a guessed one.
    .catch(() => null)
    .finally(() => inflight.delete(key));
  inflight.set(key, request);
  return request;
}

/** Test hook: forget every resolved link. */
export function clearShopUrlCache(): void {
  cache.clear();
  inflight.clear();
}

/**
 * Returns a lookup for each vehicle's Shop Tires link: its carried `shopUrl`, else the
 * server-built one once resolved, else undefined (no link rendered).
 */
export function useVehicleShopUrls(vehicles: ActiveVehicle[]): (v: ActiveVehicle) => string | undefined {
  const [, setVersion] = useState(0);
  const pendingKeys = vehicles
    .filter((v) => !v.shopUrl && hasShopSize(v))
    .map(shopUrlQuery)
    .filter((key) => !cache.has(key));
  const depKey = Array.from(new Set(pendingKeys)).sort().join("|");

  useEffect(() => {
    if (!depKey) return;
    let cancelled = false;
    const toResolve = vehicles.filter((v) => !v.shopUrl && hasShopSize(v) && !cache.has(shopUrlQuery(v)));
    Promise.all(toResolve.map(fetchShopUrl)).then(() => {
      if (!cancelled) setVersion((n) => n + 1);
    });
    return () => {
      cancelled = true;
    };
    // depKey captures exactly the vehicles still waiting on a link.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depKey]);

  return (v: ActiveVehicle) => {
    if (v.shopUrl) return v.shopUrl;
    if (!hasShopSize(v)) return undefined;
    return cache.get(shopUrlQuery(v)) ?? undefined;
  };
}
